import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, Check } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BunadCarousel from "@/components/BunadCarousel";
import BunadContactForm from "@/components/BunadContactForm";
import { getBunadById, bunader } from "@/data/bunader";

const BunadDetail = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { id } = useParams<{ id: string }>();
  const bunad = id ? getBunadById(id) : undefined;

  if (!bunad) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="section-padding pt-32">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-serif text-4xl text-foreground mb-4">Bunad ikke funnet</h1>
            <p className="text-muted-foreground mb-8">
              Beklager, vi fant ikke bunaden du leter etter.
            </p>
            <Link
              to="/#drakter"
              className="inline-flex items-center gap-2 text-primary hover:underline font-sans"
            >
              <ArrowLeft className="w-4 h-4" />
              Tilbake til alle drakter
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const heroImage = bunad.images.length > 0 ? bunad.images[0] : null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Link
              to="/#drakter"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-sans text-sm mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Tilbake til alle drakter
            </Link>

            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Image */}
              <div className="order-2 lg:order-1">
                {heroImage ? (
                  <div className="aspect-[3/4] overflow-hidden bg-muted">
                    <img
                      src={heroImage}
                      alt={bunad.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-[3/4] bg-gradient-to-br from-muted to-secondary flex items-center justify-center">
                    <span className="font-serif text-6xl text-foreground/20">
                      {bunad.title.charAt(0)}
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="order-1 lg:order-2">
                <p className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-4">
                  Våre drakter
                </p>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
                  {bunad.title}
                </h1>

                <div className="space-y-4 mb-8">
                  {bunad.fullDescription.map((paragraph, index) => (
                    <p key={index} className="text-muted-foreground font-sans leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Colors */}
                <div className="mb-8">
                  <h3 className="font-serif text-lg text-foreground mb-3">Tilgjengelige farger</h3>
                  <div className="flex flex-wrap gap-2">
                    {bunad.colors.map((color) => (
                      <span
                        key={color}
                        className="px-4 py-2 bg-secondary text-foreground font-sans text-sm rounded-full"
                      >
                        {color}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h3 className="font-serif text-lg text-foreground mb-3">Detaljer</h3>
                  <ul className="space-y-2">
                    {bunad.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3 text-muted-foreground font-sans">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery */}
        {/* Image Carousel */}
        <BunadCarousel images={bunad.images} title={bunad.title} />

        {/* Contact Form */}
        <BunadContactForm bunadTitle={bunad.title} />

        {/* Other Bunads */}
        <section className="section-padding bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-4">
                Se også
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-foreground">
                Andre drakter
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {bunader
                .filter((b) => b.id !== bunad.id)
                .slice(0, 3)
                .map((otherBunad) => (
                  <Link
                    key={otherBunad.id}
                    to={`/drakt/${otherBunad.id}`}
                    className="group block"
                  >
                    <div className="relative aspect-[3/4] bg-cream overflow-hidden mb-4">
                      {otherBunad.images.length > 0 ? (
                        <img
                          src={otherBunad.images[0]}
                          alt={otherBunad.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-muted to-secondary flex items-center justify-center">
                          <span className="font-serif text-3xl text-foreground/20 group-hover:text-primary/30 transition-colors duration-500">
                            {otherBunad.title.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/5 transition-colors duration-500" />
                    </div>
                    <h3 className="font-serif text-xl text-foreground group-hover:text-primary transition-colors">
                      {otherBunad.title}
                    </h3>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BunadDetail;
